/**
 * UTM attribution capture.
 *
 * Captures utm_* params (and referrer / landing page) from the URL on first visit
 * and persists them in localStorage for 30 days. The values are then attached to
 * the user's signup metadata so we can attribute paid ad signups by campaign / ad set.
 */

const STORAGE_KEY = "tm_utm_attribution";
const TTL_DAYS = 30;
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;

export type UtmAttribution = {
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  landing_page?: string | null;
  referrer?: string | null;
  referral_code?: string | null;
  captured_at?: string;
};

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

/**
 * Reads UTM params from the current URL. Should be called as early as possible
 * on landing pages. If any UTM param is present, the full attribution snapshot
 * is stored in localStorage. First-touch wins (won't overwrite an existing capture
 * unless TTL has expired).
 */
export function captureUtmFromUrl(): UtmAttribution | null {
  if (typeof window === "undefined") return null;

  try {
    const params = new URLSearchParams(window.location.search);
    const captured: UtmAttribution = {};
    let hasAny = false;

    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value && value.trim()) {
        captured[key] = value.trim().slice(0, 200);
        hasAny = true;
      }
    }

    // Explicit referral parameter (?ref= / ?referral=) — only when actually present.
    const ref = params.get("ref") || params.get("referral");
    if (ref && ref.trim()) {
      captured.referral_code = ref.trim().slice(0, 100);
      hasAny = true;
    }

    // No params: still record an EXTERNAL referrer (e.g. google.com, chatgpt.com)
    // as first touch. Internal navigation / own-domain referrers are ignored.
    if (!hasAny) {
      const extRef = externalReferrerHost();
      if (!extRef) return getStoredUtm();
      const existingRef = getStoredUtm();
      if (existingRef) return existingRef;
      const refOnly: UtmAttribution = {
        landing_page: window.location.pathname.slice(0, 500),
        referrer: extRef,
        captured_at: new Date().toISOString(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(refOnly));
      return refOnly;
    }

    captured.landing_page = window.location.pathname.slice(0, 500);
    try {
      captured.referrer = document.referrer ? new URL(document.referrer).hostname.slice(0, 200) : null;
    } catch {
      captured.referrer = null;
    }
    captured.captured_at = new Date().toISOString();

    // First-touch attribution: don't overwrite an existing valid capture
    const existing = getStoredUtm();
    if (existing) return existing;

    localStorage.setItem(STORAGE_KEY, JSON.stringify(captured));
    return captured;
  } catch (err) {
    console.warn("[utm] capture failed", err);
    return null;
  }
}

/** Hostname of document.referrer when it is a different site; otherwise null. */
export function externalReferrerHost(): string | null {
  try {
    if (!document.referrer) return null;
    const host = new URL(document.referrer).hostname.toLowerCase();
    const own = window.location.hostname.toLowerCase().replace(/^www\./, "");
    const h = host.replace(/^www\./, "");
    if (!h || h === own || h.endsWith("transportmate.co.uk") || h.endsWith("lovable.app") || h.endsWith("stripe.com")) return null;
    return host.slice(0, 200);
  } catch {
    return null;
  }
}

/**
 * Returns the stored UTM attribution if it exists and hasn't expired.
 */
export function getStoredUtm(): UtmAttribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as UtmAttribution;
    if (parsed.captured_at) {
      const age = Date.now() - new Date(parsed.captured_at).getTime();
      if (age > TTL_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
    }
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Clears the stored UTM attribution. Call after signup completes successfully.
 */
export function clearStoredUtm() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}
